/* Baddie Glam — motion engine
 *
 * Dependency-free. Three jobs:
 *   1. Smooth (lerped) wheel scrolling on desktop, like Lenis.
 *   2. Per-scene scroll progress written to CSS custom properties.
 *   3. Pointer tilt for 3D elements.
 *
 * JS only ever writes numbers. Every visual decision lives in CSS, driven by
 * --p / --pp / --pc / --tx / --ty.
 *
 * Progressive enhancement: <html> gets .baddie-motion only if this runs. All
 * hide-then-reveal CSS is scoped to that class, so with no JS the page renders
 * fully visible instead of blank.
 */
(function () {
  if (window.__baddieMotionLoaded) return;
  window.__baddieMotionLoaded = true;
  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  root.classList.add('baddie-motion');
  if (reduce) root.classList.add('baddie-motion--reduced');

  /* ---------------------------------------------------------------- scenes */

  var scenes = [];

  function collectScenes() {
    scenes = [].slice.call(document.querySelectorAll('[data-scene]')).map(function (el) {
      return { el: el, top: 0, height: 0, p: -1, pp: -1, visible: false };
    });
    measure();
  }

  function measure() {
    var y = window.scrollY || window.pageYOffset;
    for (var i = 0; i < scenes.length; i++) {
      var s = scenes[i];
      var r = s.el.getBoundingClientRect();
      s.top = r.top + y;
      s.height = r.height;
    }
  }

  // Two different progress values, because they answer different questions.
  //
  //   --p / --pc  travel through the viewport. 0 when the scene's top edge
  //               touches the bottom of the screen, 1 when its bottom edge
  //               leaves the top. --pc is the same remapped to -1..1 so 0
  //               means centered. Right for parallax on normal sections.
  //
  //   --pp        PINNED progress. 0 the moment a sticky stage inside the
  //               scene starts sticking, 1 the moment it stops. This is the
  //               one to use for choreography inside a pinned scene: with --p
  //               a chunk of the animation happens while the scene is still
  //               entering or already leaving, so the viewer scrolls through
  //               dead space and never sees the end of the move.
  function updateScenes(y) {
    var vh = window.innerHeight;
    for (var i = 0; i < scenes.length; i++) {
      var s = scenes[i];

      var near = s.top - y < vh * 1.5 && s.top + s.height - y > -vh * 0.5;
      if (!near) {
        if (s.visible) {
          s.visible = false;
          s.el.classList.remove('is-active');
        }
        continue;
      }
      if (!s.visible) {
        s.visible = true;
        s.el.classList.add('is-active');
      }

      var travel = vh + s.height;
      var p = (y + vh - s.top) / travel;
      p = p < 0 ? 0 : p > 1 ? 1 : p;
      p = Math.round(p * 1000) / 1000;

      // Pinned window: how far the scene can scroll while its sticky child
      // stays glued to the top. Zero or negative for scenes shorter than the
      // viewport, which simply have no pinned phase.
      var pinTravel = s.height - vh;
      var pp = pinTravel > 0 ? (y - s.top) / pinTravel : p;
      pp = pp < 0 ? 0 : pp > 1 ? 1 : pp;
      pp = Math.round(pp * 1000) / 1000;

      if (p === s.p && pp === s.pp) continue;
      s.p = p;
      s.pp = pp;

      s.el.style.setProperty('--p', p);
      s.el.style.setProperty('--pc', (p * 2 - 1).toFixed(3));
      s.el.style.setProperty('--pp', pp);
    }
  }

  /* ---------------------------------------------------------------- reveals */

  function revealAll(items) {
    for (var i = 0; i < items.length; i++) items[i].classList.add('is-revealed');
  }

  function initReveals() {
    var items = document.querySelectorAll('[data-reveal]');
    if (!items.length) return;

    for (var i = 0; i < items.length; i++) {
      items[i].style.setProperty('--reveal-index', items[i].getAttribute('data-reveal') || 0);
    }

    if (reduce || !('IntersectionObserver' in window)) {
      revealAll(items);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.01 });

    for (var k = 0; k < items.length; k++) io.observe(items[k]);

    // Failsafe. IntersectionObserver does not deliver in a hidden tab, and
    // some renderers (prerenderers, link-preview crawlers, headless
    // screenshotters) never run it at all. Without this, those clients get a
    // section frozen at opacity 0 — a blank page instead of a missed
    // animation. Timers still fire when rAF and IO do not.
    setTimeout(function () { revealAll(items); }, 2500);
  }

  /* ------------------------------------------------------------------ tilt */

  function initTilt() {
    if (reduce || !window.matchMedia('(pointer: fine)').matches) return;

    var tilts = document.querySelectorAll('[data-tilt]');
    for (var i = 0; i < tilts.length; i++) {
      (function (el) {
        if (el.dataset.tiltBound) return;
        el.dataset.tiltBound = '1';

        var raf = null;
        var nx = 0, ny = 0;

        function apply() {
          raf = null;
          el.style.setProperty('--tx', nx.toFixed(3));
          el.style.setProperty('--ty', ny.toFixed(3));
        }

        el.addEventListener('pointermove', function (e) {
          var r = el.getBoundingClientRect();
          nx = (e.clientX - r.left) / r.width * 2 - 1;
          ny = (e.clientY - r.top) / r.height * 2 - 1;
          if (!raf) raf = requestAnimationFrame(apply);
        });

        el.addEventListener('pointerleave', function () {
          nx = 0; ny = 0;
          if (!raf) raf = requestAnimationFrame(apply);
        });
      })(tilts[i]);
    }
  }

  /* ---------------------------------------------------------- smooth scroll */

  var smooth = { on: false, target: 0, current: 0 };

  function canSmooth() {
    return !reduce &&
      window.matchMedia('(pointer: fine)').matches &&
      window.matchMedia('(min-width: 990px)').matches;
  }

  function scrollLocked() {
    var bs = document.body.style;
    return bs.overflow === 'hidden' || bs.position === 'fixed' ||
      document.body.classList.contains('drawer-open') ||
      root.classList.contains('overflow-hidden');
  }

  function onWheel(e) {
    if (!smooth.on || scrollLocked()) return;
    if (e.ctrlKey) return;
    if (e.target.closest && e.target.closest('[data-no-smooth]')) return;

    e.preventDefault();

    var max = document.documentElement.scrollHeight - window.innerHeight;
    smooth.target += e.deltaY * (e.deltaMode === 1 ? 24 : 1);
    smooth.target = Math.max(0, Math.min(max, smooth.target));
  }

  function syncSmooth() {
    smooth.target = smooth.current = window.scrollY || window.pageYOffset;
  }

  /* ------------------------------------------------------------- main loop */

  function frame() {
    var y = window.scrollY || window.pageYOffset;

    if (smooth.on) {
      var d = smooth.target - smooth.current;
      if (Math.abs(d) > 0.4) {
        smooth.current += d * 0.11;
        window.scrollTo(0, smooth.current);
        y = smooth.current;
      } else if (smooth.current !== smooth.target) {
        smooth.current = smooth.target;
        window.scrollTo(0, smooth.current);
        y = smooth.current;
      }
    }

    updateScenes(y);
    requestAnimationFrame(frame);
  }

  function onScroll() {
    if (smooth.on && Math.abs((window.scrollY || 0) - smooth.current) > 4) syncSmooth();
  }

  /* ------------------------------------------------------------------ boot */

  var booted = false;

  function boot() {
    if (booted) return;
    booted = true;

    collectScenes();
    initReveals();
    initTilt();

    smooth.on = canSmooth();
    if (smooth.on) {
      syncSmooth();
      window.addEventListener('wheel', onWheel, { passive: false });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { measure(); syncSmooth(); }, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) { measure(); syncSmooth(); }
    });

    window.addEventListener('load', function () { measure(); }, { once: true });

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () { measure(); });
      ro.observe(document.body);
    }

    requestAnimationFrame(frame);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    collectScenes();
    initReveals();
    initTilt();
  });
})();
